import { createSlice } from "@reduxjs/toolkit";
import {
  createDispatcher,
  getAllDispatch,
  logisticAssignDetails,
  logisticDashboard,
  getRidersPending,
  getRiderStats,
  getRiderById,
  suspendRider,
  updateRider,
  approveRider,
  rejectRider,
  getRidersOverview,
  getAllRidersStats,
  getRecentActivities,
  getAvailableRiders,
  getRiderStatsOverview,
  getRealTimeStats,
  getIssuesReports,
  updateIssueStatus,
  getLogisticsOrders,
  getBroadcastMessages,
  createBroadcastMessage,
  updateBroadcastMessage,
  deleteBroadcastMessage,
  getIndividualMessages,
  createIndividualMessage,
  updateIndividualMessage,
  deleteIndividualMessage,
  getTargetAudienceMessages,
  createTargetAudienceMessage,
  updateTargetAudienceMessage,
  deleteTargetAudienceMessage,
  getPendingWithdrawals,
  getWithdrawalDetails,
  updatePayoutStatus,
  getEarningPayoutStats,
  getRidersPerformance,
  getRidersStats,
} from "../thunk";
import { RidersState } from "../../types/rider";
import { IssuesState } from "../../types/issues";
import { LogisticsOrdersState } from "../../types/orders";
import { CommunicationState } from "../../types/communication";
import { PayoutState } from "../../types/payout";
import { PerformanceState } from "../../types/performance";

interface LogisticsState extends RidersState, IssuesState, LogisticsOrdersState, CommunicationState, PayoutState, PerformanceState {
  allRiderInfo: any[];
  dashboard: any[];
  ordersToAssign: any[];
  createDispatcherStatus: "idle" | "loading" | "succeeded" | "failed";
}

const initialState: LogisticsState = {
  // Existing logistics state
  allRiderInfo: [],
  dashboard: [],
  ordersToAssign: [],
  createDispatcherStatus: "idle",
  
  // Rider management state
  pendingRiders: [],
  allRiders: [],
  currentRider: null,
  riderStats: null,
  ridersOverview: null,
  allRidersStats: null,
  recentActivities: [],
  recentActivitiesPagination: null,
  recentActivitiesStatus: "idle",
  // Real-time tracking state
  availableRiders: [],
  riderStatsOverview: null,
  realTimeStats: [],
  realTimeStatsPagination: null,
  realTimeTrackingStatus: "idle",
  status: "idle",
  error: null,
  
  // Individual operation statuses
  approveStatus: "idle",
  rejectStatus: "idle",
  suspendStatus: "idle",
  updateStatus: "idle",
  
  // Issues management state
  issues: [],
  currentIssue: null,
  metrics: null,
  pagination: null,
  issueUpdateStatus: "idle",
  
  // Logistics orders state
  orders: [],
  ordersPagination: {
    page: 1,
    limit: 10,
    totalRiders: 0,
    totalPages: 0,
  },
  ordersLoading: false,
  ordersError: null,
  
  // Communication state
  broadcastMessages: [],
  broadcastLoading: false,
  broadcastError: null,
  individualMessages: [],
  individualLoading: false,
  individualError: null,
  targetMessages: [],
  targetLoading: false,
  targetError: null,
  isCreatingMessage: false,
  createError: null,
  selectedMessageType: 'broadcast' as const,
  
  // Payout state
  pendingWithdrawals: [],
  currentWithdrawalDetails: null,
  payoutStats: null,
  payoutPagination: null,
  payoutStatus: "idle",
  payoutError: null,
  payoutApproveStatus: "idle",
  payoutRejectStatus: "idle",
  payoutDetailsStatus: "idle",
  
  // Performance monitoring state
  ridersPerformance: [],
  performanceStats: null,
  weeklyTrends: [],
  locationTrends: [],
  riderIssues: [],
  currentPage: 1,
  totalPages: 0,
  totalRiders: 0,
};

const logisticsSlice = createSlice({
  name: "logistics",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllDispatch.pending, (state) => {
        state.status = "loading";
        state.error = "nil";
      })
      .addCase(getAllDispatch.fulfilled, (state, action) => {
        state.allRiderInfo = action.payload;
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(getAllDispatch.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message || "Unknown error";
      })
      .addCase(logisticDashboard.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(logisticDashboard.fulfilled, (state, action) => {
        state.dashboard = action.payload;
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(logisticDashboard.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message || "Unknown error";
      })
      .addCase(logisticAssignDetails.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(logisticAssignDetails.fulfilled, (state, action) => {
        state.ordersToAssign = action.payload;
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(logisticAssignDetails.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message || "Unknown error";
      })
      .addCase(createDispatcher.pending, (state) => {
        state.createDispatcherStatus = "loading";
        state.error = null;
      })
      .addCase(createDispatcher.fulfilled, (state, action) => {
        state.createDispatcherStatus = "succeeded";
        // Don't reset the state here, as we'll explicitly fetch fresh data
        // before navigation in the component
        state.error = null;
      })
      .addCase(createDispatcher.rejected, (state, action) => {
        state.createDispatcherStatus = "failed";
        state.error = action.error?.message || "Unknown error";
      })
      
      // ===== RIDER MANAGEMENT REDUCERS =====
      
      // Get pending riders
      .addCase(getRidersPending.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getRidersPending.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.pendingRiders = action.payload.data;
        state.error = null;
      })
      .addCase(getRidersPending.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch pending riders";
      })
      
      // Get rider stats
      .addCase(getRiderStats.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getRiderStats.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.riderStats = action.payload.data;
        state.error = null;
      })
      .addCase(getRiderStats.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch rider stats";
      })
      
      // Get rider by ID
      .addCase(getRiderById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getRiderById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentRider = action.payload.data;
        state.error = null;
      })
      .addCase(getRiderById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch rider details";
      })
      
      // Suspend rider
      .addCase(suspendRider.pending, (state) => {
        state.suspendStatus = "loading";
        state.error = null;
      })
      .addCase(suspendRider.fulfilled, (state, action) => {
        state.suspendStatus = "succeeded";
        state.error = null;
        // Optionally update the rider in the lists
      })
      .addCase(suspendRider.rejected, (state, action) => {
        state.suspendStatus = "failed";
        state.error = action.payload || "Failed to suspend rider";
      })
      
      // Update rider
      .addCase(updateRider.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(updateRider.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        state.error = null;
        // Optionally update the rider in the lists
      })
      .addCase(updateRider.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.payload || "Failed to update rider";
      })
      
      // Approve rider
      .addCase(approveRider.pending, (state) => {
        state.approveStatus = "loading";
        state.error = null;
      })
      .addCase(approveRider.fulfilled, (state, action) => {
        state.approveStatus = "succeeded";
        state.error = null;
        // Remove from pending riders and update current rider
        state.pendingRiders = state.pendingRiders.filter(
          (rider) => rider._id !== action.payload.data._id
        );
        if (state.currentRider?._id === action.payload.data._id) {
          state.currentRider = action.payload.data;
        }
      })
      .addCase(approveRider.rejected, (state, action) => {
        state.approveStatus = "failed";
        state.error = action.payload || "Failed to approve rider";
      })
      
      // Reject rider
      .addCase(rejectRider.pending, (state) => {
        state.rejectStatus = "loading";
        state.error = null;
      })
      .addCase(rejectRider.fulfilled, (state, action) => {
        state.rejectStatus = "succeeded";
        state.error = null;
        // Remove from pending riders
        state.pendingRiders = state.pendingRiders.filter(
          (rider) => rider._id !== action.meta.arg
        );
      })
      .addCase(rejectRider.rejected, (state, action) => {
        state.rejectStatus = "failed";
        state.error = action.payload || "Failed to reject rider";
      })
      
      // Get riders overview
      .addCase(getRidersOverview.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getRidersOverview.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.ridersOverview = action.payload.data;
        state.error = null;
      })
      .addCase(getRidersOverview.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch riders overview";
      })
      
      // Get all riders stats
      .addCase(getAllRidersStats.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllRidersStats.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allRidersStats = {
          page: action.payload.page,
          limit: action.payload.limit,
          totalRiders: action.payload.totalRiders,
          totalPages: action.payload.totalPages,
          data: action.payload.data,
        };
        state.error = null;
      })
      .addCase(getAllRidersStats.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch all riders stats";
      })
      
      // ===== ISSUES MANAGEMENT REDUCERS =====
      
      // Get issues reports
      .addCase(getIssuesReports.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getIssuesReports.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.issues = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          totalReports: action.payload.totalReports,
          totalPages: action.payload.totalPages,
        };
        
        // Calculate metrics from the issues data
        const issues = action.payload.data;
        state.metrics = {
          openIssues: issues.filter(issue => issue.status === "Open").length,
          inProgressIssues: issues.filter(issue => issue.status === "In Progress" || issue.status === "Pending").length,
          resolvedToday: issues.filter(issue => {
            if (issue.status === "Resolved" || issue.status === "Closed") {
              const today = new Date().toDateString();
              const issueDate = issue.createdAt ? new Date(issue.createdAt).toDateString() : "";
              return issueDate === today;
            }
            return false;
          }).length,
          highPriorityIssues: issues.filter(issue => issue.issuesType === "High Priority").length,
          totalIssues: issues.length,
        };
        
        state.error = null;
      })
      .addCase(getIssuesReports.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch issues reports";
      })
      
      // Update issue status
      .addCase(updateIssueStatus.pending, (state) => {
        state.issueUpdateStatus = "loading";
        state.error = null;
      })
      .addCase(updateIssueStatus.fulfilled, (state, action) => {
        state.issueUpdateStatus = "succeeded";
        
        // Update the issue in the issues array
        const updatedIssue = action.payload.data;
        const issueIndex = state.issues.findIndex(issue => issue._id === updatedIssue._id);
        if (issueIndex !== -1) {
          state.issues[issueIndex] = {
            ...state.issues[issueIndex],
            status: updatedIssue.status,
            notes: updatedIssue.notes,
          };
        }
        
        // Update current issue if it's the one being updated
        if (state.currentIssue?._id === updatedIssue._id) {
          state.currentIssue = {
            ...state.currentIssue,
            status: updatedIssue.status,
            notes: updatedIssue.notes,
          };
        }
        
        // Recalculate metrics after status change
        if (state.issues.length > 0) {
          const issues = state.issues;
          state.metrics = {
            openIssues: issues.filter(issue => issue.status === "Open").length,
            inProgressIssues: issues.filter(issue => issue.status === "In Progress" || issue.status === "Pending").length,
            resolvedToday: issues.filter(issue => {
              if (issue.status === "Resolved" || issue.status === "Closed") {
                const today = new Date().toDateString();
                const issueDate = issue.createdAt ? new Date(issue.createdAt).toDateString() : "";
                return issueDate === today;
              }
              return false;
            }).length,
            highPriorityIssues: issues.filter(issue => issue.issuesType === "High Priority").length,
            totalIssues: issues.length,
          };
        }
        
        state.error = null;
      })
      .addCase(updateIssueStatus.rejected, (state, action) => {
        state.issueUpdateStatus = "failed";
        state.error = action.payload || "Failed to update issue status";
      })
      
      // ===== LOGISTICS ORDER DISPATCH REDUCERS =====
      
      // Get logistics orders
      .addCase(getLogisticsOrders.pending, (state) => {
        state.ordersLoading = true;
        state.ordersError = null;
      })
      .addCase(getLogisticsOrders.fulfilled, (state, action) => {
        state.ordersLoading = false;
        state.orders = action.payload.data;
        state.ordersPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          totalRiders: action.payload.totalRiders,
          totalPages: action.payload.totalPages,
        };
        state.ordersError = null;
      })
      .addCase(getLogisticsOrders.rejected, (state, action) => {
        state.ordersLoading = false;
        state.ordersError = action.payload || "Failed to fetch logistics orders";
      })
      
      // ===== COMMUNICATION CENTER REDUCERS =====
      
      // ===== BROADCAST MESSAGES =====
      
      // Get broadcast messages
      .addCase(getBroadcastMessages.pending, (state) => {
        state.broadcastLoading = true;
        state.broadcastError = null;
      })
      .addCase(getBroadcastMessages.fulfilled, (state, action) => {
        state.broadcastLoading = false;
        state.broadcastMessages = action.payload.data;
        state.broadcastError = null;
      })
      .addCase(getBroadcastMessages.rejected, (state, action) => {
        state.broadcastLoading = false;
        state.broadcastError = action.payload || "Failed to fetch broadcast messages";
      })
      
      // Create broadcast message
      .addCase(createBroadcastMessage.pending, (state) => {
        state.isCreatingMessage = true;
        state.createError = null;
      })
      .addCase(createBroadcastMessage.fulfilled, (state, action) => {
        state.isCreatingMessage = false;
        // Add new messages to the broadcast messages array
        state.broadcastMessages = action.payload.data.message;
        state.createError = null;
      })
      .addCase(createBroadcastMessage.rejected, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = action.payload || "Failed to create broadcast message";
      })
      
      // Update broadcast message
      .addCase(updateBroadcastMessage.pending, (state) => {
        state.isCreatingMessage = true;
        state.createError = null;
      })
      .addCase(updateBroadcastMessage.fulfilled, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = null;
      })
      .addCase(updateBroadcastMessage.rejected, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = action.payload || "Failed to update broadcast message";
      })
      
      // Delete broadcast message
      .addCase(deleteBroadcastMessage.pending, (state) => {
        state.isCreatingMessage = true;
        state.createError = null;
      })
      .addCase(deleteBroadcastMessage.fulfilled, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = null;
      })
      .addCase(deleteBroadcastMessage.rejected, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = action.payload || "Failed to delete broadcast message";
      })
      
      // ===== INDIVIDUAL MESSAGES =====
      
      // Get individual messages
      .addCase(getIndividualMessages.pending, (state) => {
        state.individualLoading = true;
        state.individualError = null;
      })
      .addCase(getIndividualMessages.fulfilled, (state, action) => {
        state.individualLoading = false;
        state.individualMessages = action.payload.data;
        state.individualError = null;
      })
      .addCase(getIndividualMessages.rejected, (state, action) => {
        state.individualLoading = false;
        state.individualError = action.payload || "Failed to fetch individual messages";
      })
      
      // Create individual message
      .addCase(createIndividualMessage.pending, (state) => {
        state.isCreatingMessage = true;
        state.createError = null;
      })
      .addCase(createIndividualMessage.fulfilled, (state, action) => {
        state.isCreatingMessage = false;
        // Update the individual messages array
        const existingIndex = state.individualMessages.findIndex(
          msg => msg._id === action.payload.data._id
        );
        if (existingIndex !== -1) {
          state.individualMessages[existingIndex] = action.payload.data;
        } else {
          state.individualMessages.push(action.payload.data);
        }
        state.createError = null;
      })
      .addCase(createIndividualMessage.rejected, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = action.payload || "Failed to create individual message";
      })
      
      // Update individual message
      .addCase(updateIndividualMessage.pending, (state) => {
        state.isCreatingMessage = true;
        state.createError = null;
      })
      .addCase(updateIndividualMessage.fulfilled, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = null;
      })
      .addCase(updateIndividualMessage.rejected, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = action.payload || "Failed to update individual message";
      })
      
      // Delete individual message
      .addCase(deleteIndividualMessage.pending, (state) => {
        state.isCreatingMessage = true;
        state.createError = null;
      })
      .addCase(deleteIndividualMessage.fulfilled, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = null;
      })
      .addCase(deleteIndividualMessage.rejected, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = action.payload || "Failed to delete individual message";
      })
      
      // ===== TARGET AUDIENCE MESSAGES =====
      
      // Get target audience messages
      .addCase(getTargetAudienceMessages.pending, (state) => {
        state.targetLoading = true;
        state.targetError = null;
      })
      .addCase(getTargetAudienceMessages.fulfilled, (state, action) => {
        state.targetLoading = false;
        state.targetMessages = action.payload.data;
        state.targetError = null;
      })
      .addCase(getTargetAudienceMessages.rejected, (state, action) => {
        state.targetLoading = false;
        state.targetError = action.payload || "Failed to fetch target audience messages";
      })
      
      // Create target audience message
      .addCase(createTargetAudienceMessage.pending, (state) => {
        state.isCreatingMessage = true;
        state.createError = null;
      })
      .addCase(createTargetAudienceMessage.fulfilled, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = null;
      })
      .addCase(createTargetAudienceMessage.rejected, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = action.payload || "Failed to create target audience message";
      })
      
      // Update target audience message
      .addCase(updateTargetAudienceMessage.pending, (state) => {
        state.isCreatingMessage = true;
        state.createError = null;
      })
      .addCase(updateTargetAudienceMessage.fulfilled, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = null;
      })
      .addCase(updateTargetAudienceMessage.rejected, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = action.payload || "Failed to update target audience message";
      })
      
      // Delete target audience message
      .addCase(deleteTargetAudienceMessage.pending, (state) => {
        state.isCreatingMessage = true;
        state.createError = null;
      })
      .addCase(deleteTargetAudienceMessage.fulfilled, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = null;
      })
      .addCase(deleteTargetAudienceMessage.rejected, (state, action) => {
        state.isCreatingMessage = false;
        state.createError = action.payload || "Failed to delete target audience message";
      })
      
      // ===== PAYOUT REQUEST REDUCERS =====
      
      // Get pending withdrawals
      .addCase(getPendingWithdrawals.pending, (state) => {
        state.payoutStatus = "loading";
        state.payoutError = null;
      })
      .addCase(getPendingWithdrawals.fulfilled, (state, action) => {
        state.payoutStatus = "succeeded";
        state.pendingWithdrawals = action.payload.data;
        state.payoutPagination = action.payload.pagination;
        state.payoutError = null;
      })
      .addCase(getPendingWithdrawals.rejected, (state, action) => {
        state.payoutStatus = "failed";
        state.payoutError = action.payload || "Failed to fetch pending withdrawals";
      })
      
      // Get withdrawal details
      .addCase(getWithdrawalDetails.pending, (state) => {
        state.payoutDetailsStatus = "loading";
        state.payoutError = null;
      })
      .addCase(getWithdrawalDetails.fulfilled, (state, action) => {
        state.payoutDetailsStatus = "succeeded";
        state.currentWithdrawalDetails = action.payload.data;
        state.payoutError = null;
      })
      .addCase(getWithdrawalDetails.rejected, (state, action) => {
        state.payoutDetailsStatus = "failed";
        state.payoutError = action.payload || "Failed to fetch withdrawal details";
      })
      
      // Update payout status
      .addCase(updatePayoutStatus.pending, (state, action) => {
        const actionType = action.meta.arg.data.action;
        if (actionType === 'approve') {
          state.payoutApproveStatus = "loading";
        } else if (actionType === 'reject') {
          state.payoutRejectStatus = "loading";
        }
        state.payoutError = null;
      })
      .addCase(updatePayoutStatus.fulfilled, (state, action) => {
        const actionType = action.meta.arg.data.action;
        if (actionType === 'approve') {
          state.payoutApproveStatus = "succeeded";
        } else if (actionType === 'reject') {
          state.payoutRejectStatus = "succeeded";
        }
        state.payoutError = null;
        
        // Update the withdrawal in the list
        const { riderId, withdrawalId } = action.meta.arg;
        const withdrawalIndex = state.pendingWithdrawals.findIndex(
          (w) => w.withdrawalId === withdrawalId && w.riderId === riderId
        );
        if (withdrawalIndex !== -1) {
          state.pendingWithdrawals[withdrawalIndex].status = actionType === 'approve' ? 'approved' : 'rejected';
        }
      })
      .addCase(updatePayoutStatus.rejected, (state, action) => {
        const actionType = action.meta.arg.data.action;
        if (actionType === 'approve') {
          state.payoutApproveStatus = "failed";
        } else if (actionType === 'reject') {
          state.payoutRejectStatus = "failed";
        }
        state.payoutError = action.payload || "Failed to update payout status";
      })
      
      // Get earning payout stats
      .addCase(getEarningPayoutStats.pending, (state) => {
        state.payoutStatus = "loading";
        state.payoutError = null;
      })
      .addCase(getEarningPayoutStats.fulfilled, (state, action) => {
        state.payoutStatus = "succeeded";
        state.payoutStats = action.payload.summary;
        state.payoutError = null;
      })
      .addCase(getEarningPayoutStats.rejected, (state, action) => {
        state.payoutStatus = "failed";
        state.payoutError = action.payload || "Failed to fetch earning payout stats";
      })
      
      // ===== PERFORMANCE MONITORING REDUCERS =====
      
      // Get riders performance
      .addCase(getRidersPerformance.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getRidersPerformance.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.ridersPerformance = action.payload.data;
        state.currentPage = action.payload.page || 1;
        state.totalPages = action.payload.totalPages || 0;
        state.totalRiders = action.payload.totalRiders || 0;
        state.error = null;
      })
      .addCase(getRidersPerformance.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch riders performance data";
      })
      
      // Get riders stats
      .addCase(getRidersStats.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getRidersStats.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.performanceStats = action.payload.data.stats;
        state.weeklyTrends = action.payload.data.weeklyTrends;
        state.locationTrends = action.payload.data.locationTrends;
        state.riderIssues = action.payload.data.issues;
        state.error = null;
      })
      .addCase(getRidersStats.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch rider performance statistics";
      })
      
      // ===== RECENT ACTIVITIES REDUCERS =====
      
      // Get recent activities
      .addCase(getRecentActivities.pending, (state) => {
        state.recentActivitiesStatus = "loading";
        state.error = null;
      })
      .addCase(getRecentActivities.fulfilled, (state, action) => {
        state.recentActivitiesStatus = "succeeded";
        state.recentActivities = action.payload.data;
        state.recentActivitiesPagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getRecentActivities.rejected, (state, action) => {
        state.recentActivitiesStatus = "failed";
        state.error = action.payload || "Failed to fetch recent activities";
      })
      
      // ===== REAL-TIME TRACKING REDUCERS =====
      
      // Get available riders
      .addCase(getAvailableRiders.pending, (state) => {
        state.realTimeTrackingStatus = "loading";
        state.error = null;
      })
      .addCase(getAvailableRiders.fulfilled, (state, action) => {
        state.realTimeTrackingStatus = "succeeded";
        state.availableRiders = action.payload.riders;
        state.error = null;
      })
      .addCase(getAvailableRiders.rejected, (state, action) => {
        state.realTimeTrackingStatus = "failed";
        state.error = action.payload || "Failed to fetch available riders";
      })
      
      // Get rider stats overview
      .addCase(getRiderStatsOverview.pending, (state) => {
        state.realTimeTrackingStatus = "loading";
        state.error = null;
      })
      .addCase(getRiderStatsOverview.fulfilled, (state, action) => {
        state.realTimeTrackingStatus = "succeeded";
        state.riderStatsOverview = action.payload.data;
        state.error = null;
      })
      .addCase(getRiderStatsOverview.rejected, (state, action) => {
        state.realTimeTrackingStatus = "failed";
        state.error = action.payload || "Failed to fetch rider stats overview";
      })
      
      // Get real-time stats
      .addCase(getRealTimeStats.pending, (state) => {
        state.realTimeTrackingStatus = "loading";
        state.error = null;
      })
      .addCase(getRealTimeStats.fulfilled, (state, action) => {
        state.realTimeTrackingStatus = "succeeded";
        state.realTimeStats = action.payload.data;
        state.realTimeStatsPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          totalRiders: action.payload.totalRiders,
          totalPages: action.payload.totalPages,
        };
        state.error = null;
      })
      .addCase(getRealTimeStats.rejected, (state, action) => {
        state.realTimeTrackingStatus = "failed";
        state.error = action.payload || "Failed to fetch real-time stats";
      });
  },
});

export default logisticsSlice.reducer;
